/**
 * The **Arena juice sound cues** (Arena Slice F, PRD #334, ADR-0178): the pure
 * decision of which sound an elite entrance, the boss entrance, and the level-up
 * draft opening play - the {@link swarmCue}/{@link stormCue} pattern (the caller
 * owns *when*; this module only picks *what*).
 *
 * All three deliberately **reuse already-registered sounds** rather than adding
 * registry rows (the slice's reuse-where-they-fit rule), so no new bake/stub
 * surface exists and the stub discipline is satisfied by construction:
 *
 * - **Elite** = a randomized `vortex-attack-*` wind gust - the Arena elite *is* a
 *   killable energy vortex (ADR-0173), so its entrance sounds like its own wind.
 * - **Boss** = the `storm-start` Blackthorn growl - the Arena boss *is* Lord
 *   Blackthorn, the same body the storm event announces with this growl.
 * - **Level-up fanfare** = a composed ascending run of the same `xp-pickup`
 *   plink the orbs absorb with, resolved by the `level-up-fanfare` cap note
 *   (UO `sfx15`, its own registered row with a real committed bake - the one
 *   exception to this module's reuse-only rule) - the orb grammar completing
 *   its scale (see {@link arenaLevelUpFanfare}).
 *
 * Full volume: these are owner-side klaxons in a private single-listener run,
 * not sounds positioned in a shared world. Playback routes through
 * {@link AudioManager}, so the master mute governs them; a stubbed (silent) wav
 * degrades to nothing, like every other cue.
 */
import type { SoundEvent } from './soundObserver';

/** Arena cues are private-run klaxons for the one runner, never proximity-scaled. */
export const ARENA_CUE_VOLUME = 1;

/** The registered vortex wind gusts the elite sting draws from (ADR-0148/0152). */
const ELITE_GUSTS = ['vortex-attack-1', 'vortex-attack-2', 'vortex-attack-3'] as const;

/** The elite-entrance sting: a randomized energy-vortex wind gust. */
export function arenaEliteCue(rng: () => number = Math.random): SoundEvent {
  const pick = ELITE_GUSTS[Math.min(ELITE_GUSTS.length - 1, Math.floor(rng() * ELITE_GUSTS.length))]!;
  return { sound: pick, volume: ARENA_CUE_VOLUME };
}

/** The boss-entrance sting: Blackthorn's own spawn growl. */
export function arenaBossCue(): SoundEvent {
  return { sound: 'storm-start', volume: ARENA_CUE_VOLUME };
}

/** One note of a composed cue: what to play and when, relative to the trigger. */
export interface TimedCue {
  delayMs: number;
  cue: SoundEvent;
}

/** How many rising XP plinks open the level-up fanfare. */
export const LEVEL_UP_PLINK_COUNT = 5;

/** Gap between the fanfare's plinks (ms) - fast enough to read as one run. */
export const LEVEL_UP_PLINK_SPACING_MS = 70;

/** The run climbs a full octave: the last plink plays at twice the first's rate. */
const LEVEL_UP_PLINK_TOP_RATE = 2;

/** The plinks swell from the orbs' ambient volume up toward the chime's. */
const LEVEL_UP_PLINK_START_VOLUME = 0.5;
const LEVEL_UP_PLINK_END_VOLUME = 0.85;

/** The resolving chime lands just off the last plink, capping the run. */
export const LEVEL_UP_CHIME_DELAY_MS = LEVEL_UP_PLINK_COUNT * LEVEL_UP_PLINK_SPACING_MS + 60;

/**
 * The level-up fanfare, sounded as the draft opens (the pause makes it
 * readable): an ascending arpeggio of the same `xp-pickup` plink every orb
 * absorb plays - rate-ramped a full octave so it reads as the gathering
 * grammar cresting - resolved by the `level-up-fanfare` cap note (UO
 * `sfx15`, a dedicated registered row with a real committed bake).
 * Deterministic (no rng): the level-up is a fixed fanfare, not an ambient
 * scatter. The caller owns the clock; each entry's `delayMs` is relative to
 * the trigger instant.
 */
export function arenaLevelUpFanfare(): readonly TimedCue[] {
  const notes: TimedCue[] = [];
  for (let i = 0; i < LEVEL_UP_PLINK_COUNT; i++) {
    const k = i / (LEVEL_UP_PLINK_COUNT - 1);
    notes.push({
      delayMs: i * LEVEL_UP_PLINK_SPACING_MS,
      cue: {
        sound: 'xp-pickup',
        volume: LEVEL_UP_PLINK_START_VOLUME + k * (LEVEL_UP_PLINK_END_VOLUME - LEVEL_UP_PLINK_START_VOLUME),
        rate: LEVEL_UP_PLINK_TOP_RATE ** k,
      },
    });
  }
  notes.push({
    delayMs: LEVEL_UP_CHIME_DELAY_MS,
    cue: { sound: 'level-up-fanfare', volume: ARENA_CUE_VOLUME },
  });
  return notes;
}

/** XP plinks sit under the combat mix - they fire constantly, so they must never fatigue. */
export const ARENA_XP_CUE_VOLUME = 0.5;

/** Half-octave of upward pitch variance, so a pouring pile of plinks sparkles. */
const XP_CUE_RATE_SPREAD = 0.5;

/**
 * The XP-orb absorb plink (VS-style XP orbs): one per orb-cluster arrival at
 * the runner, pitch-varied upward of the recorded sample so a pile pouring in
 * over a few frames reads as a glittering run of notes, never a machine gun.
 * The only cue that uses {@link SoundEvent.rate}. Unlike the klaxons above it
 * is deliberately under full volume - it fires on every pickup, all run long.
 */
export function arenaXpCue(rng: () => number = Math.random): SoundEvent {
  return {
    sound: 'xp-pickup',
    volume: ARENA_XP_CUE_VOLUME,
    rate: 1 + rng() * XP_CUE_RATE_SPREAD,
  };
}

/** The combo tier stinger's volume: a one-shot punch (at most one per tier per
 * run), under the entrance klaxons so a tier-up reads as escalation, not alarm. */
export const ARENA_COMBO_STINGER_VOLUME = 0.7;

/** Each tier crossing pitches the stinger up this fraction over the last, so the
 * one family of sounds encodes the tier by rising rate (the xp-pickup lesson). */
export const ARENA_COMBO_STINGER_RATE_STEP = 0.16;

/**
 * The kill-streak combo tier stinger (PRD #432 Slice B): a warm bell plink - the
 * `xp-pickup` chime the XP orbs already register - pitched up one
 * {@link ARENA_COMBO_STINGER_RATE_STEP} per tier so crossing 5 / 10 / 25 / 50 /
 * 100 rings progressively higher (one family, pitch encodes tier;
 * {@link SoundEvent.rate}, the xp-pickup precedent it now shares outright). This
 * replaced the original `sword7` Kryss clang, which read as an aggressive metallic
 * "ching" rather than a reward - a milestone should feel *pleasant*. Per-kill
 * ticks are deliberately never played - a melt would white-noise; the combo's
 * whole sonic identity is these one-shot tier stingers, mixed above the orb
 * plinks by volume so a tier-up still punches through the pickup stream. `tier`
 * is 1-based (the machine's `recordKill` return); defensively floored at 1 so a
 * bad value still rings. Reuses a registered sound (no new bake/stub surface).
 */
export function arenaComboStingerCue(tier: number): SoundEvent {
  const t = Math.max(1, Math.floor(tier));
  return {
    sound: 'xp-pickup',
    volume: ARENA_COMBO_STINGER_VOLUME,
    rate: 1 + (t - 1) * ARENA_COMBO_STINGER_RATE_STEP,
  };
}

/**
 * The low-HP heartbeat (hit-feedback PRD #430 Slice D, ADR-0227): one lub-dub
 * per {@link ../arenaDanger.ARENA_HEARTBEAT_THROTTLE_MS} throttle window while
 * the runner is under the danger threshold. The volume is resolved by the
 * caller ({@link ../arenaDanger.heartbeatVolume} ramps it as HP falls - this
 * module stays the pure what-to-play decision); clamped here to the
 * AudioManager's [0, 1] gain contract. The `heartbeat` row is the xp-pickup
 * precedent: a committed offline-synthesized wav mapped to a UO basename
 * (VERIFY) so a captain bake can replace it.
 */
export function arenaHeartbeatCue(volume: number): SoundEvent {
  return { sound: 'heartbeat', volume: Math.min(1, Math.max(0, volume)) };
}

/**
 * The Vacuum grab whoosh (PRD #431 Slice C): one full-volume cue as the runner
 * claims a vacuum and the whole gem field inrushes - the jackpot's signature.
 * Sounds *once* per grab (the per-gem arrival plinks still ride
 * {@link arenaXpCue} as the field pours in). The committed wav is an offline-
 * synthesized whoosh mapped to a UO gust basename (VERIFY), the xp-pickup
 * precedent.
 */
export function arenaVacuumCue(): SoundEvent {
  return { sound: 'vacuum-grab', volume: 1 };
}
